import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { Users, ShoppingBag, DollarSign, BarChart3, Settings, Star, Zap, MessageSquare, Package, Crown, CalendarDays, Layers, TrendingUp, LogOut, FileText, LayoutGrid, Target, Megaphone, Store } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useUser } from '../contexts/UserContext';
import { OrdersTab } from '../components/astrologer-dashboard/OrdersTab';
import { ProductsTab } from '../components/astrologer-dashboard/ProductsTab';
import { ChatTab } from '../components/astrologer-dashboard/ChatTab';
import { FinancesTab } from '../components/astrologer-dashboard/FinancesTab';
import { StatsTab } from '../components/astrologer-dashboard/StatsTab';
import { PromotionTab } from '../components/astrologer-dashboard/PromotionTab';
import { ProfileTab } from '../components/astrologer-dashboard/ProfileTab';
import { MarketplaceTab } from '../components/astrologer-dashboard/MarketplaceTab';
import { AutoServiceDetail } from '../components/astrologer-dashboard/AutoServiceDetail';
interface AstrologerDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function AstrologerDashboardPage({
  onNavigate
}: AstrologerDashboardPageProps) {
  const {
    user,
    logout
  } = useUser();
  const [activeTab, setActiveTab] = useState('orders');
  const [activeAutoService, setActiveAutoService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };
  const menuItems = [{
    id: 'orders',
    label: 'Заказы',
    icon: <ShoppingBag className="w-5 h-5" data-id="element-16718" />
  }, {
    id: 'products',
    label: 'Мои услуги',
    icon: <Package className="w-5 h-5" data-id="element-16719" />
  }, {
    id: 'marketplace',
    label: 'Мой маркетплейс',
    icon: <Store className="w-5 h-5" data-id="element-16720" />
  }, {
    id: 'chat',
    label: 'Чат с клиентами',
    icon: <MessageSquare className="w-5 h-5" data-id="element-16721" />
  }, {
    id: 'finances',
    label: 'Финансы',
    icon: <DollarSign className="w-5 h-5" data-id="element-16722" />
  }, {
    id: 'stats',
    label: 'Статистика',
    icon: <BarChart3 className="w-5 h-5" data-id="element-16723" />
  }, {
    id: 'promotion',
    label: 'Продвижение',
    icon: <Zap className="w-5 h-5" data-id="element-16724" />
  }, {
    id: 'profile',
    label: 'Профиль',
    icon: <Settings className="w-5 h-5" data-id="element-16725" />
  }];
  const renderContent = () => {
    if (activeAutoService) {
      return <AutoServiceDetail activeAutoService={activeAutoService} setActiveAutoService={setActiveAutoService} showToast={showToast} data-id="element-16726" />;
    }
    switch (activeTab) {
      case 'orders':
        return <OrdersTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-16727" />;
      case 'products':
        return <ProductsTab showToast={showToast} data-id="element-16728" />;
      case 'marketplace':
        return <MarketplaceTab showToast={showToast} data-id="element-16729" />;
      case 'chat':
        return <ChatTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-16730" />;
      case 'finances':
        return <FinancesTab showToast={showToast} data-id="element-16731" />;
      case 'stats':
        return <StatsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-16732" />;
      case 'promotion':
        return <PromotionTab showToast={showToast} setActiveAutoService={setActiveAutoService} data-id="element-16733" />;
      case 'profile':
        return <ProfileTab showToast={showToast} onNavigate={onNavigate} data-id="element-16734" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-16735">
            <p data-id="element-16736">
              Раздел "{menuItems.find(m => m.id === activeTab)?.label}" в
              разработке
            </p>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-16737">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-16738">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-16739">
          {/* Sidebar */}
          <div className="lg:col-span-3" data-id="element-16740">
            {/* Mobile: horizontal scrollable tabs */}
            <div className="lg:hidden mb-6" data-id="element-16741">
              <div className="flex items-center justify-between mb-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-16742">
                <div className="flex items-center gap-3" data-id="element-16743">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-lg font-bold text-indigo-700 flex-shrink-0 uppercase" data-id="element-16744">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div data-id="element-16745">
                    <div className="font-bold text-slate-900 text-base" data-id="element-16746">
                      {user?.name || 'Астролог'}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5" data-id="element-16747">
                      <Badge variant="top" size="sm" className="bg-amber-100/50 text-amber-700 border-amber-200/50" data-id="element-16748">
                        <Crown className="w-3 h-3 mr-1" data-id="element-16749" /> Базовый план
                      </Badge>
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-xs px-3 border-indigo-200 text-indigo-600 hover:bg-indigo-50" icon={<Zap className="w-3 h-3" data-id="element-16751" />} onClick={() => setActiveTab('promotion')} data-id="element-16750">
                  Улучшить
                </Button>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-16752">
                {menuItems.map(item => <button key={item.id} onClick={() => {
                setActiveTab(item.id);
                setActiveAutoService(null);
              }} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-primary' : 'bg-white border border-slate-200 text-slate-600'}`} data-id="element-16753">
                    <span className={`${activeTab === item.id && !activeAutoService ? 'text-primary' : 'text-slate-400'}`} data-id="element-16754">
                      {cloneElement(item.icon as React.ReactElement, {
                    className: 'w-3.5 h-3.5'
                  })}
                    </span>
                    {item.label}
                  </button>)}
              </div>
            </div>

            {/* Desktop: vertical sidebar */}
            <Card padding="none" className="sticky top-24 overflow-hidden hidden lg:block" data-id="element-16755">
              <div className="p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-16756">
                <div className="flex flex-col items-center text-center" data-id="element-16757">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-700 mb-3 uppercase" data-id="element-16758">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div className="font-bold text-slate-900 text-lg mb-2" data-id="element-16759">
                    {user?.name || 'Астролог'}
                  </div>
                  <div className="w-full bg-amber-50 border border-amber-100 rounded-xl p-3 flex flex-col items-center gap-2" data-id="element-16760">
                    <div className="flex items-center text-amber-700 font-medium text-sm" data-id="element-16761">
                      <Crown className="w-4 h-4 mr-1.5" data-id="element-16762" /> Базовый план
                    </div>
                    <Button variant="outline" size="sm" fullWidth className="text-xs border-amber-200 text-amber-700 hover:bg-amber-100/50 bg-white" icon={<Zap className="w-3 h-3" data-id="element-16764" />} onClick={() => setActiveTab('promotion')} data-id="element-16763">
                      Улучшить план
                    </Button>
                  </div>
                </div>
              </div>
              <nav className="p-2" data-id="element-16765">
                {menuItems.map(item => <button key={item.id} onClick={() => {
                setActiveTab(item.id);
                setActiveAutoService(null);
              }} className={`w-full flex items-center px-4 py-3 mb-1 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-primary' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-16766">
                    <span className={`mr-3 ${activeTab === item.id && !activeAutoService ? 'text-primary' : 'text-slate-400'}`} data-id="element-16767">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>)}
                <div className="pt-2 mt-1 border-t border-slate-100" data-id="element-16768">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-16769">
                    <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-16770" />
                    Выйти
                  </button>
                </div>
              </nav>

              {/* Paid Services Block */}
              <div className="p-4 border-t border-slate-100" data-id="element-16771">
                {/* Section 1: Astrologer's products for clients */}
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5 px-1" data-id="element-16772">
                  Мои продукты
                </div>
                <div className="space-y-1.5 mb-4" data-id="element-16773">
                  <button onClick={() => setActiveTab('products')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-purple-50/70 border border-purple-100 hover:bg-purple-50 transition-colors text-left" data-id="element-16774">
                    <CalendarDays className="w-4 h-4 text-purple-500 flex-shrink-0" data-id="element-16775" />
                    <div className="flex-1 min-w-0" data-id="element-16776">
                      <div className="text-xs font-semibold text-slate-800 truncate" data-id="element-16777">
                        Астро-календарь
                      </div>
                      <div className="text-[10px] text-purple-600 font-medium" data-id="element-16778">
                        67 продаж • 1 490 ₽
                      </div>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Активен" data-id="element-16779"></div>
                  </button>
                  <button onClick={() => setActiveTab('products')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-emerald-50/70 border border-emerald-100 hover:bg-emerald-50 transition-colors text-left" data-id="element-16780">
                    <Layers className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-16781" />
                    <div className="flex-1 min-w-0" data-id="element-16782">
                      <div className="text-xs font-semibold text-slate-800 truncate" data-id="element-16783">
                        Пакеты разборов
                      </div>
                      <div className="text-[10px] text-emerald-600 font-medium" data-id="element-16784">
                        22 продажи
                      </div>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Активен" data-id="element-16785"></div>
                  </button>
                </div>

                {/* Section 2: Platform auto-services */}
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5 px-1 pt-3 border-t border-slate-100" data-id="element-16786">
                  Авто-услуги
                </div>
                <div className="space-y-1 mb-2" data-id="element-16787">
                  {[{
                  key: 'natal',
                  name: 'Натальная',
                  icon: FileText,
                  used: 153,
                  total: 1000,
                  active: true
                }, {
                  key: 'solar',
                  name: 'Соляр',
                  icon: Star,
                  used: 47,
                  total: 500,
                  active: true
                }, {
                  key: 'synastry',
                  name: 'Синастрия',
                  icon: Users,
                  used: 89,
                  total: 200,
                  active: true
                }, {
                  key: 'combo',
                  name: 'Комбо',
                  icon: Layers,
                  used: 0,
                  total: 0,
                  active: false
                }, {
                  key: 'forecast',
                  name: 'Транзиты',
                  icon: TrendingUp,
                  used: 0,
                  total: 0,
                  active: false
                }, {
                  key: 'calendar',
                  name: 'Календарь',
                  icon: CalendarDays,
                  used: 0,
                  total: 0,
                  active: false
                }].map(s => {
                  const remaining = s.total - s.used;
                  const pct = s.total > 0 ? s.used / s.total * 100 : 0;
                  const isLow = s.total > 0 && remaining < s.total * 0.15;
                  const isSelected = activeAutoService === s.key;
                  return <button key={s.name} onClick={() => {
                    setActiveAutoService(s.key);
                  }} className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors text-left ${isSelected ? 'bg-sky-50 ring-1 ring-sky-200' : s.active ? 'hover:bg-sky-50' : 'hover:bg-slate-50 opacity-60'}`} data-id="element-16788">
                        <s.icon className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-primary' : s.active ? 'text-sky-500' : 'text-slate-400'}`} data-id="element-16789" />
                        <div className="flex-1 min-w-0" data-id="element-16790">
                          <div className="flex items-center justify-between mb-0.5" data-id="element-16791">
                            <span className={`text-[11px] font-medium truncate ${isSelected ? 'text-primary' : 'text-slate-700'}`} data-id="element-16792">
                              {s.name}
                            </span>
                            <span className={`text-[10px] font-bold flex-shrink-0 ml-1 ${s.active ? isLow ? 'text-rose-500' : 'text-slate-700' : 'text-slate-400'}`} data-id="element-16793">
                              {s.active ? `${remaining}` : '—'}
                            </span>
                          </div>
                          {s.active && <div className="w-full bg-slate-100 rounded-full h-1" data-id="element-16794">
                              <div className={`h-1 rounded-full ${isLow ? 'bg-rose-400' : pct > 50 ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{
                          width: `${pct}%`
                        }} data-id="element-16795" />
                            </div>}
                        </div>
                      </button>;
                })}
                </div>
                <button onClick={() => {
                setActiveAutoService(null);
                setActiveTab('promotion');
              }} className="w-full text-[10px] font-medium text-primary hover:text-indigo-700 transition-colors text-center py-1 mb-3" data-id="element-16796">
                  Управление пакетами →
                </button>

                {/* Section 3: Platform advertising */}
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5 px-1 pt-3 border-t border-slate-100" data-id="element-16797">
                  Реклама
                </div>
                <div className="space-y-1.5" data-id="element-16798">
                  <button onClick={() => setActiveTab('promotion')} className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left" data-id="element-16799">
                    <LayoutGrid className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-16800" />
                    <div className="text-[11px] text-slate-500 truncate flex-1" data-id="element-16801">
                      Баннер на главной
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" data-id="element-16802"></div>
                  </button>
                  <button onClick={() => setActiveTab('promotion')} className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left" data-id="element-16803">
                    <Target className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-16804" />
                    <div className="text-[11px] text-slate-500 truncate flex-1" data-id="element-16805">
                      Закрепление в каталоге
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" data-id="element-16806"></div>
                  </button>
                  <button onClick={() => setActiveTab('promotion')} className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left" data-id="element-16807">
                    <Megaphone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-16808" />
                    <div className="text-[11px] text-slate-500 truncate flex-1" data-id="element-16809">
                      Рассылка по базе
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" data-id="element-16810"></div>
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9" data-id="element-16811">
            <SectionErrorBoundary key={activeTab} sectionName={`astrologer-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-16812">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-16813">
          {toast}
        </div>}
    </div>;
}